import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import Layout from "../../../components/Layout";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal";
import BreadCrumb from "../../../components/BreadCrumb";
import useStyles from "../styles";
import { httprequest } from "../../../data/api";
import { toast } from "react-toastify";
import FileDropZone from "./components/FileDropZone";
import MaterialPreviewModal from "../components/MaterialPreviewModal";
import uploadFile, { formatFileSize, getFileMeta } from "./utils/uploadFile";

const SYLLABUS_ENDPOINT = "/api/v1/syllabus";

const SyllabusTopicDetails = () => {
  const { subjectId, syllabusId, topicId } = useParams();
  const { pathname } = useLocation();
  const classes = useStyles();

  const isEmployee = pathname.includes("/employee/");
  const syllabusLink = isEmployee
    ? `/class-activities/employee/syllabus/${subjectId}`
    : `/class-activities/employer/syllabus/${subjectId}/${syllabusId}`;

  const [topic, setTopic] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [isAddResourceOpen, setIsAddResourceOpen] = useState(false);
  const [resourceFiles, setResourceFiles] = useState([]);
  const [isUploadingResource, setIsUploadingResource] = useState(false);

  const [deleteMaterialId, setDeleteMaterialId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewMaterial, setPreviewMaterial] = useState(null);

  const getTopic = () => {
    setIsLoading(true);
    httprequest.get(`${SYLLABUS_ENDPOINT}/topics/${topicId}`)
      .then((res) => {
        setTopic(res?.data?.data);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.message);
      });
  };

  useEffect(() => {
    getTopic();
  }, [topicId]);

  const progress = topic?.progress;
  const isCompleted = progress === "COMPLETED";
  const isNotStarted = !progress || progress === "NOT_STARTED";

  const handleStartTopic = () => {
    setIsActionLoading(true);
    httprequest.patch(`${SYLLABUS_ENDPOINT}/topics/${topicId}/start`)
      .then((res) => {
        toast.success(res?.data?.message || "Topic started");
        setIsActionLoading(false);
        getTopic();
      })
      .catch((err) => {
        setIsActionLoading(false);
        toast.error(err?.response?.data?.message);
      });
  };

  const handleCompleteTopic = () => {
    setIsActionLoading(true);
    httprequest.patch(`${SYLLABUS_ENDPOINT}/topics/${topicId}/complete`)
      .then((res) => {
        toast.success(res?.data?.message || "Topic marked as complete");
        setIsActionLoading(false);
        getTopic();
      })
      .catch((err) => {
        setIsActionLoading(false);
        toast.error(err?.response?.data?.message);
      });
  };

  const openAddResourceModal = () => {
    setResourceFiles([]);
    setIsAddResourceOpen(true);
  };

  const closeAddResourceModal = () => {
    setResourceFiles([]);
    setIsAddResourceOpen(false);
  };

  const handleUploadResources = async () => {
    if (resourceFiles.length === 0) return;
    setIsUploadingResource(true);
    try {
      await Promise.all(resourceFiles.map(async (file) => {
        const uploaded = await uploadFile(file);

        return httprequest.post(`${SYLLABUS_ENDPOINT}/topics/${topicId}/materials`, uploaded);
      }));
      toast.success("Resource(s) added successfully");
      setIsUploadingResource(false);
      closeAddResourceModal();
      getTopic();
    } catch (err) {
      setIsUploadingResource(false);
      toast.error(err?.response?.data?.message);
    }
  };

  const handleConfirmDeleteMaterial = () => {
    if (!deleteMaterialId) return;
    setIsDeleting(true);
    httprequest.delete(`${SYLLABUS_ENDPOINT}/topics/${topicId}/materials/${deleteMaterialId}`)
      .then((res) => {
        toast.success(res?.data?.message || "Resource deleted successfully");
        setIsDeleting(false);
        setDeleteMaterialId(null);
        getTopic();
      })
      .catch((err) => {
        setIsDeleting(false);
        toast.error(err?.response?.data?.message);
      });
  };

  const materials = topic?.materials || [];

  return (
    <Layout
      children={
        <>
          <BreadCrumb
            link_one={syllabusLink}
            link_one_name="Syllabus"
            active={topic?.title || ""}
          />

          {!isLoading && topic && (
            <div className="page-content" style={{ marginTop: "20px", textAlign: "left" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <h4 style={{ margin: 0, textAlign: "left" }}>Topic: {topic?.title}</h4>
                {isCompleted ? (
                  <span className={`${classes.statusBadge} ${classes.statusBadgePublished}`}>
                    <Icon icon="mdi:check-circle" style={{ marginRight: 4 }} /> Completed
                  </span>
                ) : isNotStarted ? (
                  !isEmployee && (
                    <Button
                      buttonName="Start Topic"
                      variant="secondaryoutline"
                      color="btnsecondary"
                      onClick={handleStartTopic}
                      isLoading={isActionLoading}
                    />
                  )
                ) : (
                  <Button
                    buttonName="Mark Complete"
                    buttonIcon={<Icon icon="mdi:check" style={{ marginRight: 6 }} />}
                    variant="successoutline"
                    color="btnsuccess"
                    onClick={handleCompleteTopic}
                    isLoading={isActionLoading}
                  />
                )}
              </div>

              <div style={{ marginTop: 20 }}>
                <h4 style={{ marginBottom: 6, textAlign: "left" }}>Objectives</h4>
                <p style={{ color: "#667085", margin: 0, textAlign: "left" }}>{topic?.objective}</p>
              </div>

              <div style={{ marginTop: 24 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: "#98A2B3", letterSpacing: 0.5, textAlign: "left" }}>RESOURCES</p>

                {materials.length === 0 ? (
                  <div className={classes.emptyStateContainer}>
                    <div className={classes.emptyStateIcon}><Icon icon="mdi:folder-outline" /></div>
                    <h3 className={classes.emptyStateTitle}>No Resources available</h3>
                    <p className={classes.emptyStateSubtitle}>
                      There are no materials uploaded for this topic yet.
                    </p>
                    <Button
                      buttonName="Add Resources"
                      buttonIcon={<Icon icon="mdi:plus" style={{ marginRight: 6 }} />}
                      variant="primary"
                      color="btndefault"
                      onClick={openAddResourceModal}
                    />
                  </div>
                ) : (
                  <>
                    <div className={classes.materialsGrid}>
                      {materials.map((material) => {
                        const meta = getFileMeta(material?.file_name);

                        return (
                          <div key={material?.id} className={classes.materialCard} onClick={() => setPreviewMaterial(material)}>
                            <div className={classes.materialIcon} style={{ background: meta.bg, color: meta.color }}>
                              <Icon icon={meta.icon} />
                            </div>
                            <div style={{ minWidth: 0, textAlign: "left" }}>
                              <p className={classes.materialTitle}>{material?.title}</p>
                              <p className={classes.materialMeta}>{meta.label} . {formatFileSize(material?.file_size)}</p>
                            </div>
                            <div className={classes.materialActions}>
                              <Icon icon="mdi:download-outline" onClick={(e) => {
                                e.stopPropagation();
                                window.open(material?.file_url, "_blank");
                              }}
                              />
                              <Icon icon="mingcute:delete-2-line" onClick={(e) => {
                                e.stopPropagation();
                                setDeleteMaterialId(material?.id);
                              }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        buttonName="Add Resources"
                        buttonIcon={<Icon icon="mdi:plus" style={{ marginRight: 6 }} />}
                        variant="primary"
                        color="btndefault"
                        onClick={openAddResourceModal}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Add Resources Modal */}
          <Modal
            title="Add Resources"
            subtitle={<div>Upload material for this topic</div>}
            modalContent={
              <FileDropZone
                files={resourceFiles}
                onFilesSelected={(files) => setResourceFiles((prev) => [...prev, ...files])}
                onRemoveFile={(index) => setResourceFiles((prev) => prev.filter((_, i) => i !== index))}
              />
            }
            modalFooter={
              <div className={classes.modalFooterBtn}>
                <Button buttonName="Cancel" onClick={closeAddResourceModal} variant="primaryoutline" color="btnfontprimary" buttonSize="fluid" />
                <Button
                  buttonName="Add Material"
                  variant="primary"
                  color="btndefault"
                  buttonSize="fluid"
                  onClick={handleUploadResources}
                  isLoading={isUploadingResource}
                  disabled={resourceFiles.length === 0}
                />
              </div>
            }
            isopen={isAddResourceOpen}
            closeModal={closeAddResourceModal}
          />

          {/* Delete confirmation */}
          <Modal
            title="Confirm Delete"
            subtitle={<div>Are you sure you want to delete this resource? This action cannot be undone.</div>}
            modalContent={<div />}
            modalFooter={
              <div className={classes.modalFooterBtn}>
                <Button buttonName="Cancel" onClick={() => setDeleteMaterialId(null)} variant="primaryoutline" color="btnfontprimary" buttonSize="fluid" />
                <Button
                  buttonName="Delete"
                  variant="danger"
                  color="btndanger"
                  buttonSize="fluid"
                  onClick={handleConfirmDeleteMaterial}
                  isLoading={isDeleting}
                />
              </div>
            }
            isopen={Boolean(deleteMaterialId)}
            closeModal={() => setDeleteMaterialId(null)}
          />

          <MaterialPreviewModal
            isOpen={!!previewMaterial}
            closeModal={() => setPreviewMaterial(null)}
            material={previewMaterial}
          />
        </>
      }
    />
  );
};

export default SyllabusTopicDetails;
